// ui/composables/useChargeMaster.js
import { computed, ref } from 'vue';

import { viewerToday } from './viewerToday';
import { commitChargeMasterVersion } from '../../application/useCases/commitChargeMasterVersion';
import { createVersionTimeline, currentAsOf, scheduledAsOf } from '../../domain/model/VersionTimeline';
import { hasCharge as hasChargeOn, isOnSheet } from '../../domain/services/chargeMasterResolution';
import { soleSeriesOf } from '../../domain/services/chargeMasterSeries';
import {
  appendChargeMasterVersion,
  createChargeMaster,
  loadChargeMasterVersions,
} from '../../infrastructure/api/chargeMasterApi';

/**
 * A clinic's charge master, as the read page and the editor work with it.
 *
 * THE CONTEXT'S COMPOSITION ROOT for charge masters — the only place that binds the Firestore
 * adapters to the repository port. Everything above it depends on the port; nothing above it imports
 * `infrastructure/`. Same role `useFundingSourceVersions` plays for payers, and the clinic is
 * supplied per operation rather than captured at construction, because a page creating this in
 * `setup()` may not have its clinic yet.
 *
 * IT READS THE CLOCK ONCE, captured at creation and never re-read. A dialog left open across
 * midnight keeps deciding against the date it opened on: which sheet is in force, the floor under
 * the date picker, and what a save resolves against all stay consistent with what the user is
 * looking at. Every rule beneath takes `asOf` as an argument, which is what makes them testable
 * without freezing time.
 *
 * ONE SERIES, AND THE AMBIGUITY IS SURFACED RATHER THAN ASSUMED AWAY. The read is by clinic, so if a
 * clinic ever acquires a second charge master this would hold two series at once. `soleSeriesOf`
 * detects that, `isAmbiguous` says so, and the screens refuse to show or edit anything rather than
 * displaying charges drawn from whichever series happened to have the later date.
 */

/**
 * @param {{ asOf?: string }} [options] - `asOf` defaults to today and is accepted only so a test or
 *   a caller with its own session date can supply one.
 * @returns {Object}
 */
export function useChargeMaster({ asOf = viewerToday() } = {}) {
  /**
   * The port, satisfied by the Firestore adapters. Assembled here rather than imported as a ready
   * object so the binding is visible at the one place that owns it.
   *
   * @type {import('../../application/ports/ChargeMasterRepository.port').ChargeMasterRepository}
   */
  const repository = {
    loadVersions: loadChargeMasterVersions,
    create: createChargeMaster,
    appendVersion: appendChargeMasterVersion,
  };

  /** @type {import('vue').Ref<import('../../domain/model/ChargeMaster.types').ChargeMaster[]>} */
  const sheets = ref([]);
  const isBusy = ref(false);
  const hasLoaded = ref(false);

  const series = computed(() => soleSeriesOf(sheets.value));

  /** True when the clinic holds more than one charge master and none can be resolved. */
  const isAmbiguous = computed(() => series.value.isAmbiguous);

  /** True once loaded and the clinic has never saved a charge master. */
  const hasNoChargeMaster = computed(() => (
    hasLoaded.value && !isAmbiguous.value && series.value.chargeMasterId === null
  ));

  /** Coordinates only — the sheet behind an entry is found in `sheets` by id. */
  const timeline = computed(() => createVersionTimeline(series.value.versions));

  const inForceEntry = computed(() => currentAsOf(timeline.value, asOf));
  const scheduledEntries = computed(() => scheduledAsOf(timeline.value, asOf));

  const sheetOf = (entry) => sheets.value.find(
    (sheet) => sheet.versionId === entry?.versionId,
  ) ?? null;

  /**
   * The sheet that applies on the working date, or null.
   *
   * NULL IS AN ANSWER — no charge master was in force on this date. It never falls forward to a
   * sheet that starts later: charging a February service at a March charge is the silent rewrite the
   * whole pattern exists to prevent. The page shows "nothing configured for this date" rather than
   * the newest sheet it can find.
   */
  const inForce = computed(() => sheetOf(inForceEntry.value));

  /** The next sheet already scheduled to start, if there is one. */
  const scheduled = computed(() => sheetOf(scheduledEntries.value[0]));

  return {
    asOf,
    sheets,
    timeline,
    inForce,
    scheduled,
    inForceEntry,
    scheduledEntries,
    isBusy,
    hasLoaded,
    isAmbiguous,
    hasNoChargeMaster,

    /**
     * Loads every sheet the clinic has, tombstones included.
     *
     * @param {{ clinicId: string }} scope
     * @returns {Promise<void>}
     */
    async load({ clinicId }) {
      isBusy.value = true;
      try {
        sheets.value = await repository.loadVersions({ clinicId });
        hasLoaded.value = true;
      } finally {
        isBusy.value = false;
      }
    },

    /**
     * Saves the sheet as of a date, creating the clinic's charge master if it has none.
     *
     * ONE ACTION FOR BOTH, because the caller cannot usefully tell them apart — the use case decides
     * from what storage holds. Reloads afterwards so the timeline the editor shows reflects what was
     * actually written, including anything a colleague landed in the meantime.
     *
     * @param {{ clinicId: string, effectiveFrom: string,
     *   record: import('../../application/ports/ChargeMasterRepository.port').ChargeMasterRecord }}
     *   request
     * @returns {Promise<{ chargeMasterId: string, versionId: string }>}
     */
    async commit({ clinicId, effectiveFrom, record }) {
      isBusy.value = true;
      try {
        const written = await commitChargeMasterVersion({
          clinicId, asOf, effectiveFrom, record,
        }, repository);

        sheets.value = await repository.loadVersions({ clinicId });
        hasLoaded.value = true;

        return written;
      } finally {
        isBusy.value = false;
      }
    },
  };
} // useChargeMaster()

/**
 * What this clinic's charge master says about a code, as of a date.
 *
 * THE ONLY CHARGE-MASTER SURFACE PUBLISHED OUTSIDE THIS CONTEXT, and deliberately the narrowest one
 * that answers the questions other screens actually have. The treatment-service editor offers codes
 * the clinic bills on and warns about one that has no charge; it has no business holding a sheet, a
 * timeline, or a commit action. Handing it the full composable would publish four things so one
 * screen could ask two questions.
 *
 * TWO QUESTIONS, NOT ONE, and conflating them is the mistake this shape prevents. "Is this code on
 * the charge master" decides what a picker may OFFER; "does it have a charge" decides whether to
 * WARN. A code on the sheet with no charge yet is a legitimate mid-setup state: it is offered, and
 * it warns.
 *
 * IT ANSWERS, IT DOES NOT DECIDE. Neither answer blocks a save. A service whose code has since left
 * the charge master still resolves, still saves, and warns that it will hold at billing — history
 * does not become uneditable because a charge line went away.
 *
 * @param {{ asOf?: string }} [options]
 * @returns {{ load: (scope: { clinicId: string }) => Promise<void>, isLoading: import('vue').Ref,
 *   hasLoaded: import('vue').Ref, isOnChargeMaster: (serviceCodeId: string) => boolean,
 *   hasCharge: (serviceCodeId: string) => boolean }}
 */
export function useChargeMasterCodes({ asOf = viewerToday() } = {}) {
  const chargeMaster = useChargeMaster({ asOf });

  return {
    isLoading: chargeMaster.isBusy,

    /**
     * Published so a caller can tell "not on the charge master" from "not read yet". Narrowing a
     * picker before the answer arrives would empty it and read as a clinic that bills on nothing.
     */
    hasLoaded: chargeMaster.hasLoaded,

    /** @param {{ clinicId: string }} scope */
    load: (scope) => chargeMaster.load(scope),

    /**
     * Does the clinic bill on this code at all, charged or not? What a picker offers.
     *
     * @param {string|null|undefined} serviceCodeId
     * @returns {boolean}
     */
    isOnChargeMaster: (serviceCodeId) => isOnSheet(chargeMaster.inForce.value, serviceCodeId),

    /**
     * False before anything is loaded, and false when no sheet applies on the date — which is the
     * same answer billing gives, so a screen never claims a code is charged when it is not.
     *
     * @param {string|null|undefined} serviceCodeId
     * @returns {boolean}
     */
    hasCharge: (serviceCodeId) => hasChargeOn(chargeMaster.inForce.value, serviceCodeId),
  };
} // useChargeMasterCodes()
